<template>
    <div :class="props.class">
        <ConfirmationModal v-if="askConfirmation" :title="confirmationTitle" :text="confirmationText" @emitResponse="checkConfirmationResponse" />
        <LoadingDialog v-if="loading" />
        
        <Dialog 
            v-model:visible="moduleVideoResourseSaveVisible" 
            modal 
            header="Gerenciar materiais de apoio" 
            :draggable="false" 
            :style="{ width: '50rem' }"
            @hide="closeDialog()"
        >
            <div class="my-5">
                <LoaderComponent v-if="loadingCourseModuleVideoResourses" />
                <div v-if="!loadingCourseModuleVideoResourses">
                    <p v-if="courseModuleVideoResourses.length < 1">Nenhum material de apoio</p>
                    <template v-for="(videoResourse, index) of courseModuleVideoResourses" :key="index">
                        <div class="p-2 my-2 border border-iron rounded flex justify-between text-white font-semibold flex-wrap flex-col md:flex-nowrap md:flex-row bg-blueberry">
                            <div class="w-full md:w-auto">
                                {{ videoResourse.title }}.{{ videoResourse.file_path.split('.')[1] }}
                            </div> 
                            <div class="flex flex-wrap justify-center md:justify-between w-full md:w-auto md:flex-row mt-5 mb:mt-0">
                                <Button
                                    class="no-bg-button mr-2"
                                    :icon="'pi pi-download'"
                                    @click="getResourseFile(videoResourse.id, `${videoResourse.title}.${videoResourse.file_path.split('.')[1]}`)"
                                    v-tooltip.top="'Download arquivo'" 
                                />
                                <Button
                                    class="no-bg-button"
                                    :icon="'pi pi-trash'"
                                    @click="removeCourseVideoResourse(videoResourse.id)"
                                    v-tooltip.top="'Remover vídeo'" 
                                />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div class="flex items-center gap-4 mb-8">
                <label for="description" class="font-semibold w-24">Título <RequiredMarker :isValid="resourseDataToValidate.title" /></label>
                <InputText 
                    v-model="moduleVideoResourse.title" 
                    id="description" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <div class="flex items-center gap-4 mb-8">
                <label for="videoFile" class="font-semibold w-24">Arquivo <RequiredMarker :isValid="resourseDataToValidate.file" /></label>
                <div class="flex justify-between w-full">
                    <FileUpload
                        ref="fileUpload"
                        name="video"
                        mode="basic"
                        :maxFileSize="maxFileSize"
                        chooseLabel="Adicionar arquivo"
                        @select="onSelectResourseFile"
                        class="bg-passion-fruit-forced"
                    />
                </div>
            </div>
            <div class="flex justify-end gap-2">
                <Button label="Cancel" severity="secondary" @click="closeDialog()"></Button>
                <Button class="no-bg-button" label="Save" @click.prevent="saveModuleVideoResourse()"></Button>
            </div>
        </Dialog>
    </div>
</template>
<script setup>
    import { defineProps, onMounted, defineEmits, reactive, ref, watch } from 'vue';
    import { useToast } from 'primevue/usetoast';
    import { showToast } from '@/composables/toast';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';
    import Dialog from 'primevue/dialog';
    import Button from 'primevue/button';
    import FileUpload from 'primevue/fileupload';
    import InputText from 'primevue/inputtext';
    import { isTextValid } from '@/composables/textUtils';
    import utils from '@/composables/utils';
    import CourseVideoResourseService from '@/services/CourseVideoResourseService';

    const toast = useToast();
    const props = defineProps({
        videoId: {
            type: Number,
            required: true
        },
        class: {
            type: String,
            required: false,
            default: ''
        }
    });

    const fileUpload = ref(null);
    let loading = ref(false);
    let videoId = ref(null);
    let moduleVideoResourseSaveVisible = ref(false);
    let moduleVideoResourse = ref({
        title: null,
        file: null,
        course_module_video: null
    });

    const emit = defineEmits(["actionPerformed"]);

    const maxFileSize = 1 * 1024 * 1024 * 1024;
    onMounted(() => {
        videoId.value = props.videoId;
        isVisible();
        getCourseModuleVideoResourses(true);
        resetVideoResourceObject();
    });

    function isVisible(){
        moduleVideoResourseSaveVisible.value = videoId.value != null ? true : false;
    }

    function closeDialog() {
        videoId.value = null;
        moduleVideoResourseSaveVisible.value = false;
        emit('closed', true);
    }

    watch(() => props.videoId, (newValue) => {
        videoId.value = newValue;
        isVisible();
        getCourseModuleVideoResourses(true);
        resetVideoResourceObject();
    },
        { deep: true }
    );

    let resourseDataToValidate = reactive({
        title: true,
        file: true
    })
    let resourseErrorMessages = {
        title: 'Por favor informe o titulo do arquivo',
        file: 'Por favor informe um arquivo'
    }
    let selectedVideoResourseFile = ref(null);
    async function saveModuleVideoResourse(){
        let isValid = true;
        for(let attr of Object.keys(resourseDataToValidate)){
            if(!isTextValid(moduleVideoResourse.value[attr])){
                isValid = false;
                resourseDataToValidate[attr] = false;
                showToast(toast, resourseErrorMessages[attr], 'error');
                break;
            }else{
                resourseDataToValidate[attr] = true;
            }
        }
        if(!isValid){
            return;
        }
        let videoResourseData = utils.copyVariable(moduleVideoResourse.value);
        if(videoResourseData.course_module_video == null)
            videoResourseData.course_module_video = videoIdOfResourses.value;
        const formData = new FormData();
        for(let attr of Object.keys(videoResourseData)){
            if(attr == 'file'){
                formData.append('videoFile', moduleVideoResourse.value.file);
            }else{
                formData.append(attr, videoResourseData[attr]);
            }
        }

        const uploadResponse = await CourseVideoResourseService.saveVideoResourse(formData, videoId.value); 

        if(!uploadResponse.success()){
            showToast(toast, uploadResponse.getMessage(), 'error');
            return;
        }
        showToast(toast, 'Arquivo adicionado');
        getCourseModuleVideoResourses(true);
        resetVideoResourceObject();
        if (fileUpload.value) {
            fileUpload.value.clear();
        }
    }

    function resetVideoResourceObject(){
        moduleVideoResourse.value = {
            title: null,
            file: null,
            course_module_video: null
        };
    }
    
    function onSelectResourseFile(event) {
        selectedVideoResourseFile.value = event.files[0];
        if(selectedVideoResourseFile.value == undefined){
            showToast(toast, "Um problema ocorreu ao selecionar o arquivo, tente novamente", 'error');
            selectedVideoResourseFile.value = null;
        }else if (selectedVideoResourseFile.value.size > maxFileSize) {
            showToast(toast, "O tamanho do vídeo não pode exceder 1 GB.", 'error');
            selectedVideoResourseFile.value = null;
        }
        moduleVideoResourse.value.file = [null, undefined, ''].includes(selectedVideoResourseFile.value) ? null : selectedVideoResourseFile.value;
    }

    let videoIdOfResourses = ref(null);

    let courseModuleVideoResourses = ref([]);
    let loadingCourseModuleVideoResourses = ref(false);

    async function getCourseModuleVideoResourses(){
        loadingCourseModuleVideoResourses.value = true;
        
        let videoResourses = videoId.value ? await CourseVideoResourseService.getVideoResourse(videoId.value) : [];

        loadingCourseModuleVideoResourses.value = false;

        courseModuleVideoResourses.value = utils.parseToArray(videoResourses);
    }

    async function removeCourseVideoResourse(videoResourseId = ''){
        loading.value = true;
        const response = await CourseVideoResourseService.removeResourse(videoResourseId);
        loading.value = false;

        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }
        showToast(toast, response.getMessage());

        getCourseModuleVideoResourses(true);
        if (fileUpload.value) {
            fileUpload.value.clear();
        }
    }

    async function getResourseFile(videoResourseId = '', fileName = ''){
        const response = await CourseVideoResourseService.fetchDonwloadData(videoResourseId);
        if(!response.success()){
            showToast(toast, response.message, 'error');
            return;
        }

        const url = window.URL.createObjectURL(new Blob([response.getContent()]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }
</script>

<style scoped>
    .no-bg-button{
        border: 1px solid var(--passion-fruit)!important;
        background-color: #fff!important;
        color: var(--passion-fruit)!important; 
    }
    .no-bg-button:hover{
        border: 1px solid var(--dark-passion-fruit)!important;
        color: var(--dark-passion-fruit)!important;
        transition: 0.2s ease;
    }
</style>